import "./domain-process-types.scss";
import { useContext, useEffect, useState } from "react";

import ConfirmationPopup from "../../../shared/components/confirmation-popup/confirmation-popup";
import Spinner from "../../../shared/components/spinner/spinner";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import { useHttpNonTable } from "../../../shared/hooks/use-httpNonTable";
import { getAllDataNonTable, postSingle } from "../../../shared/utils/api";
import { usePrompt } from "../../../shared/utils/use-prompt";
import { createUUID, validateLoadingStatusNew } from "../../../shared/utils/utils";
import { ITableRowData } from "../../components/data-table/data-table";
import ProcessTypeSelector from "./process-type-selector/process-type-selector";
import ProcessTypeConfiguration from "./process-type-configuration/process-type-configuration";
import DomainTypeSelector from "./domain-type-selector/domain-type-selector";

const DomainProcessTypes = () => {
  const authCtx = useContext(AuthContext);

  const [selectedDomainType, setSelectedDomainType] = useState<string>("");
  const [selectedProcessType, setSelectedProcessType] = useState<ITableRowData | undefined>(undefined);
  const [processTypes, setProcessTypes] = useState<ITableRowData[]>([]);
  const [processTypeToBeChanged, setProcessTypeToBeChanged] = useState<string>("");
  const [isDirty, setIsDirty] = useState(false);
  const { flag, setFlag, next } = usePrompt(isDirty);
  const [isDomainTypeUpdated, setIsDomainTypeUpdated] = useState(false);

  const {
    sendRequest: getProcessTypes,
    status: getProcessTypesStatus,
    data: getProcessTypesData,
    error: getProcessTypesError
  } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, undefined, "manager");

  const { 
    sendRequest: postProcessType, 
    status: postProcessTypeStatus, 
    error: postProcessTypeError, 
    data: postProcessTypeData 
  } = useHttp(postSingle, false, authCtx.token, "processType",  "manager");

  useEffect(() => {
    if (postProcessTypeStatus === "completed" && !postProcessTypeError && postProcessTypeData) {
      setProcessTypes(processTypes.map(pt => pt.id === postProcessTypeData.id ? postProcessTypeData : pt));
      if (processTypeToBeChanged) {
        setSelectedProcessType(processTypes.find(pt => pt.id === processTypeToBeChanged));
      } else {
        setSelectedProcessType(postProcessTypeData);
      }
      setProcessTypeToBeChanged("");
      setFlag(false);
      
      if (isDirty) {
        next();
      }

      setIsDirty(false);

    }
  }, [postProcessTypeData, postProcessTypeStatus, postProcessTypeError]);

  useEffect(() => {
    if (selectedDomainType) {
      getProcessTypes(`domain_type/${selectedDomainType}/process_types?order_by=name asc`);
    }
  }, [selectedDomainType]);

  useEffect(() => {
    if (getProcessTypesData && getProcessTypesStatus === "completed" && !getProcessTypesError) {
      setProcessTypes(getProcessTypesData);

      if (getProcessTypesData.length > 0) {
        setSelectedProcessType(getProcessTypesData[0]);
      } else {
        setSelectedProcessType(undefined);
      }
      setIsDomainTypeUpdated(false);
    }
  }, [getProcessTypesData, getProcessTypesStatus, getProcessTypesError]);

  const handleSelectProcessType = (processType: ITableRowData) => {
    if (processType.id === selectedProcessType?.id) {
      return;
    }

    if (!processTypes.some((pt: ITableRowData) => pt.id === processType.id)) {
      setProcessTypes([...processTypes, processType]);
    }

    if (isDirty) {
      setFlag(true);
      setProcessTypeToBeChanged(processType.id);
      return;
    }

    setSelectedProcessType(processType);
    setProcessTypeToBeChanged("");

    if (processType.id !== "" && processTypes[processTypes.length - 1].id === "") {
      setProcessTypes(processTypes.slice(0, processTypes.length - 1));
    }
  };

  const selectDomainType = (domainTypeId: string) => {
    setSelectedDomainType(domainTypeId);
  };

  const processTypeUpdated = (updatedProcessType: ITableRowData) => {
    setIsDirty(true);
    setSelectedProcessType(updatedProcessType);
  };

  const handlePopupContinue = (flag: boolean) => {
    setFlag(flag);
    setIsDirty(false);
    setSelectedProcessType(processTypes.find(pt => pt.id === processTypeToBeChanged));
    setProcessTypeToBeChanged("");
    if (processTypeToBeChanged !== "" && processTypes[processTypes.length - 1].id === "") {
      setProcessTypes(processTypes.slice(0, processTypes.length - 1));
    }
    next();
  };

  const handlePopupSave = () => {
    handleSaveProcessType();
  };

  const handleSaveProcessType = () => {
    const newProcessType = {
      ...selectedProcessType,
      id: !selectedProcessType?.id ? createUUID() : selectedProcessType?.id,
      domain_type_id: selectedDomainType
    };
    if (selectedProcessType?.id === "") {
      setProcessTypes(processTypes.map(pt => pt.id === selectedProcessType?.id ? newProcessType : pt));
    }
    postProcessType(`process_type/${newProcessType.id}`, newProcessType);
  };

  return <div className="domainProcessTypes"> 
    {validateLoadingStatusNew([getProcessTypesStatus, postProcessTypeStatus]) && <Spinner />}
    <ConfirmationPopup
      show={flag}
      handleSaveClick={handlePopupSave}
      handlePopupClosed={() => setFlag(false)}
      handleContinue={() => handlePopupContinue(false)} />
    <div className="domainProcessTypes-domainTypeBar">
      <DomainTypeSelector onSelectDomainType={selectDomainType} isDirty={isDirty} setFlag={setFlag} isDomainTypeUpdated={setIsDomainTypeUpdated}/>
    </div>
    <div className="domainProcessTypes-processTypeBar">
      <ProcessTypeSelector selectedProcessType={selectedProcessType?.id} onSelectProcessType={handleSelectProcessType} processTypes={processTypes}/>
    </div>
    <div className="domainProcessTypes-processTypeConfig">
      <ProcessTypeConfiguration domainTypeId={selectedDomainType} processType={selectedProcessType} onProcessTypeUpdated={processTypeUpdated} onSaveProcessType={handleSaveProcessType} isDomainTypeUpdated={isDomainTypeUpdated} setIsDomainTypeUpdated={setIsDomainTypeUpdated}/>
    </div>
  </div>;
};

export default DomainProcessTypes;

